/** Building blocks for the vault screens (Phase 9). */
import { Field, Input, Notice, cn } from "@custos/ui";
import { vaultPasswordProblem } from "@custos/vault";
import { Eye, EyeOff } from "lucide-react";
import { useState, type InputHTMLAttributes } from "react";

/** Password input with a show/hide toggle. Never autocompleted from other sites' passwords. */
export function SecretInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement> & { id: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        type={shown ? "text" : "password"}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className={cn("pr-12", className)}
      />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? "Hide password" : "Show password"}
        aria-controls={props.id}
        className="absolute top-1/2 right-1.5 inline-flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-muted hover:bg-hover hover:text-fg"
      >
        {shown ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
      </button>
    </div>
  );
}

/** New vault password + repeat. Calls onChange with the password once both are valid, else null. */
export function NewPasswordFields({
  onChange,
  touched,
  label = "Vault password",
}: {
  onChange: (password: string | null) => void;
  touched: boolean;
  label?: string;
}) {
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const problem = vaultPasswordProblem(password);
  const mismatch = repeat !== password;

  const update = (p: string, r: string) => {
    setPassword(p);
    setRepeat(r);
    onChange(!vaultPasswordProblem(p) && p === r ? p : null);
  };

  return (
    <>
      <Field
        label={label}
        hint="At least 10 characters. Used only on this device to encrypt your vault."
        error={touched && problem ? problem : null}
      >
        {(p) => (
          <SecretInput
            {...p}
            value={password}
            onChange={(e) => update(e.target.value, repeat)}
            autoComplete="new-password"
          />
        )}
      </Field>
      <Field label="Repeat password" error={touched && mismatch ? "The passwords don't match." : null}>
        {(p) => (
          <SecretInput
            {...p}
            value={repeat}
            onChange={(e) => update(password, e.target.value)}
            autoComplete="new-password"
          />
        )}
      </Field>
    </>
  );
}

/** Numbered recovery phrase words. */
export function WordGrid({ words, className }: { words: string[]; className?: string }) {
  return (
    <ol
      className={cn("grid grid-cols-2 gap-2 sm:grid-cols-3", className)}
      aria-label="Recovery phrase"
      data-testid="recovery-phrase"
    >
      {words.map((w, i) => (
        <li
          key={i}
          className="flex min-h-11 items-center gap-2 rounded-field border border-line bg-soft px-3 font-mono text-sm text-fg"
        >
          <span className="w-5 text-right text-xs text-muted tabular-nums">{i + 1}</span>
          <span data-testid="recovery-word">{w}</span>
        </li>
      ))}
    </ol>
  );
}

export function BrowserVaultWarning() {
  return (
    <Notice tone="warning" title="A browser is the least safe place for keys">
      Keep only small amounts in this vault. Anyone who gets your recovery phrase can take the funds, and if
      you lose the phrase and forget your password, <b>nobody can recover them — not even us</b>. The Chrome
      extension and the apps (coming later) store keys more safely.
    </Notice>
  );
}
